export interface Workbook {
  number: string;
  title: string;
  description: string;
  tag: string;
  coverImage: string;
}

export const workbooks: Workbook[] = [
  {
    number: '01',
    title: 'The Matter & Change Workbook',
    description:
      'A visual, classroom-ready resource covering matter, particle models, physical and chemical changes, and the foundational concepts students need to explain what they observe.',
    tag: '60 pages · 6 units',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/87WDowN752kkxPpK7GE4sC/volume_01_mockup_optimized.webp',
  },
  {
    number: '02',
    title: 'Atomic Structure & Trends Tracker',
    description:
      'Builds understanding of atomic structure, electron arrangement, the periodic table, and periodic trends through visual explanations and guided practice.',
    tag: '60 pages · 6 units',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/2zxudKHTWG9agHVJpvsMAz/volume_02_mockup_optimized.webp',
  },
  {
    number: '03',
    title: 'The Bonding & Lewis Script',
    description:
      'Covers electron configurations, ionic and covalent bonding, Lewis structures, and VSEPR molecular geometry, with guided drawing spaces and orbital-diagram practice.',
    tag: '60 pages · 6 units',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/fJUgtWy9dWEh26XNaoz4bk/volume_03_mockup_optimized.webp',
  },
  {
    number: '04',
    title: 'Reaction & Stoichiometry Lab-on-Paper',
    description:
      'Covers chemical nomenclature, equation balancing, mole relationships, and stoichiometric calculations, with scaffolded problem-solving spaces and paper-based lab simulations for independent practice.',
    tag: '60 pages · 6 units',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/uUYdjZM5P3U95XBaDqcokx/volume_04_mockup_optimized.webp',
  },
  {
    number: '05',
    title: 'Organic Chemistry & Bio-Systems Action Book',
    description:
      'Introduces organic chemistry foundations, functional groups, polymers, basic biochemistry, and environmental chemistry through case studies and applied problem-solving activities.',
    tag: '60 pages · 6 units',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/eXZgNKSvrALAivoCFsr6kX/volume_05_mockup_optimized.webp',
  },
];

export interface Bonus {
  badge: string;
  icon: string;
  title: string;
  description: string;
  value: string;
}

export const bonuses: Bonus[] = [
  {
    badge: 'BONUS 1',
    icon: '📋',
    title: 'The Dual-Version Assessment Vault',
    description:
      'A complete bank of A/B assessments for every Chemistry topic. Same difficulty. Different data. Zero copying. Instant retakes. Version A for practice. Version B for the test. Zero prep time.',
    value: '$47 USD',
  },
  {
    badge: 'BONUS 2',
    icon: '🎨',
    title: 'Science Comics Engagement Pack',
    description:
      '10 original Chemistry comic stories with comprehension activities and science connection pages. For the students who think they hate Chemistry — until they read these.',
    value: '$47 USD',
  },
  {
    badge: 'BONUS 3',
    icon: '🔬',
    title: 'The Visual Lab Station Playbook',
    description:
      '7 complete lab protocols with step-by-step procedures, pre-formatted data sheets, safety checklists and a universal lab report template. Walk in. Print. Run the lab.',
    value: '$47 USD',
  },
  {
    badge: 'BONUS 4',
    icon: '📚',
    title: 'The Visual Chemistry Cheat-Sheet Library',
    description:
      '150+ visual reference cards covering every major Chemistry concept. Project them. Print them. Hand them out. Perfect for review, scaffolding and exam preparation.',
    value: '$47 USD',
  },
];

export interface Testimonial {
  text: string;
  name: string;
  city: string;
  role: string;
  initials: string;
}

export const testimonials: Testimonial[] = [
  {
    text: 'I used to spend every Sunday rebuilding the same stoichiometry lesson. With this system I printed Workbook 4 on Friday and walked in Monday completely ready. My students stayed engaged the entire class. That never happens.',
    name: 'Sarah M.',
    city: 'Houston, Texas',
    role: 'High School Chemistry Teacher',
    initials: 'SM',
  },
  {
    text: 'The Lab Station Playbook fixed the part of my week I dreaded most. Safety checklist, procedure, data sheet — all printed in one go. My labs finally run on time.',
    name: 'David O.',
    city: 'Manchester, United Kingdom',
    role: 'Head of Science, Secondary School',
    initials: 'DO',
  },
  {
    text: 'The A/B assessment vault alone paid for the whole thing. Same difficulty, different data. Retakes stopped being a Sunday project.',
    name: 'Amelia K.',
    city: 'Toronto, Ontario',
    role: 'Grade 11 & 12 Chemistry Teacher',
    initials: 'AK',
  },
  {
    text: 'I teach four sections of Chemistry. Having every worksheet, lab guide and review sheet in one printable system cut my prep from hours to minutes.',
    name: 'Marcus T.',
    city: 'Phoenix, Arizona',
    role: 'High School Chemistry Teacher',
    initials: 'MT',
  },
  {
    text: 'The Lewis structure drawing zones were the turning point. Students who had given up on bonding started finishing the page on their own.',
    name: 'Priya N.',
    city: 'Birmingham, United Kingdom',
    role: 'Chemistry Teacher, Years 10–13',
    initials: 'PN',
  },
  {
    text: 'I printed the Science Comics on a whim before a Friday period. Half the class asked if there were more. In Chemistry. On a Friday.',
    name: 'Jenna R.',
    city: 'Columbus, Ohio',
    role: 'High School Science Teacher',
    initials: 'JR',
  },
  {
    text: 'The materials helped me stop combining worksheets from multiple sources and gave me one organized system to use across my chemistry classes.',
    name: 'Luis A.',
    city: 'Vancouver, British Columbia',
    role: 'Chemistry & Physics Teacher',
    initials: 'LA',
  },
];

export interface Notification {
  name: string;
  location: string;
  time: string;
}

export const notifications: Notification[] = [
  { name: 'Sarah M.', location: 'Houston, Texas', time: '2 minutes ago' },
  { name: 'David O.', location: 'Manchester, United Kingdom', time: '5 minutes ago' },
  { name: 'Amelia K.', location: 'Toronto, Ontario', time: '8 minutes ago' },
  { name: 'Marcus T.', location: 'Phoenix, Arizona', time: '11 minutes ago' },
  { name: 'Priya N.', location: 'Birmingham, United Kingdom', time: '14 minutes ago' },
  { name: 'Jenna R.', location: 'Columbus, Ohio', time: '18 minutes ago' },
  { name: 'Luis A.', location: 'Vancouver, British Columbia', time: '22 minutes ago' },
  { name: 'Rachel B.', location: 'Denver, Colorado', time: '26 minutes ago' },
  { name: 'Tom H.', location: 'Leeds, United Kingdom', time: '31 minutes ago' },
  { name: 'Nicole F.', location: 'Calgary, Alberta', time: '35 minutes ago' },
  { name: 'Eric S.', location: 'Portland, Oregon', time: '41 minutes ago' },
  { name: 'Hannah W.', location: 'Glasgow, Scotland', time: '47 minutes ago' },
  { name: 'Andre P.', location: 'Atlanta, Georgia', time: '52 minutes ago' },
  { name: 'Megan L.', location: 'Ottawa, Ontario', time: '58 minutes ago' },
  { name: 'Chris D.', location: 'Bristol, United Kingdom', time: '1 hour ago' },
  { name: 'Olivia G.', location: 'Minneapolis, Minnesota', time: '1 hr 8 min ago' },
  { name: 'Ben C.', location: 'Cardiff, Wales', time: '1 hr 15 min ago' },
  { name: 'Tanya V.', location: 'Sacramento, California', time: '1 hr 24 min ago' },
  { name: 'Peter M.', location: 'Halifax, Nova Scotia', time: '1 hr 33 min ago' },
  { name: 'Grace I.', location: 'Nashville, Tennessee', time: '1 hr 45 min ago' },
];

export interface Faq {
  question: string;
  answer: string;
}

export const faqs: Faq[] = [
  {
    question: 'What exactly will I receive?',
    answer:
      'You will receive immediate digital access to all five Visual Chemistry™ teaching volumes and the bonus resources included with your purchase.',
  },
  {
    question: 'Is this a physical product?',
    answer: 'No. Visual Chemistry™ is a digital product. No physical materials will be shipped.',
  },
  {
    question: 'How will I receive the materials?',
    answer:
      'After completing your purchase, you will receive access through the email address entered during checkout. Please check your inbox, spam, and junk folders.',
  },
  {
    question: 'Do I need to pay a monthly subscription?',
    answer:
      'No. Visual Chemistry™ requires a one-time payment. There are no monthly membership or subscription fees.',
  },
  {
    question: 'Will I keep access to the materials?',
    answer:
      'Yes. Your purchase includes lifetime access to the digital materials included in the offer.',
  },
  {
    question: 'Are answer keys included?',
    answer: 'Yes. Answer keys are included for the corresponding activities and assessments.',
  },
  {
    question: 'Which grade levels is it designed for?',
    answer:
      'The resources are designed primarily for middle and high school chemistry classes and can be adapted to different student levels and teaching programs.',
  },
  {
    question: 'Can I use the resources with my students?',
    answer:
      'Yes. The resources are designed for classroom use by the purchasing teacher. Redistribution, resale, or public sharing of the files is not permitted.',
  },
];
